import type { AuthProviderId, B2cConfig } from "./authProviders";

/**
 * Azure AD B2C ROPC (Resource Owner Password Credentials) client.
 *
 * Mirrors Provider Portal's client-side pattern: POSTs email/password to the
 * B2C token endpoint and receives an access + refresh token. Only works
 * against a B2C user flow that has ROPC enabled (typically a dedicated
 * `b2c_1a_ropc_*` policy).
 *
 * Tokens are cached in localStorage namespaced by provider so the two B2C
 * providers can coexist without stomping each other.
 */

export interface B2cTokens {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_at: number; // epoch seconds, computed at store time
  token_type: string;
  /**
   * Epoch seconds the REFRESH token itself expires, computed at store time
   * from B2C's `refresh_token_expires_in` (ACT-1247 SSO-PP auth-linking
   * design doc §5b — "the load-bearing config" for the whole "link once"
   * promise). Undefined when the token response didn't include the field
   * (e.g. a stash written before this existed) — callers must not assume
   * "undefined means far in the future or never".
   */
  refresh_token_expires_at?: number;
  /**
   * The email this record was AUTHENTICATED with (normalized: trim +
   * lowercase), set by `ropcLogin` from its own `email` argument — never
   * decoded out of `id_token`. This is the security anchor ppToken.ts's
   * `getPpToken` binds a stashed token to (see its module doc comment):
   * claim-shape-independent, so it works even against a B2C policy (e.g.
   * Provider Portal's ROPC policy) that emits no `emails`/
   * `preferred_username` claim at all. `ropcRefresh` carries the existing
   * value forward (a refresh never changes who the token belongs to).
   */
  boundIdentityEmail?: string;
  /**
   * True when the access token in this record was minted by an INTERACTIVE
   * login (`ropcLogin` — the PpLoginModal re-link / primary B2C sign-in),
   * false when it was derived from a SILENT `ropcRefresh` grant.
   *
   * Load-bearing for the Create-Auth eligibility pre-flight's re-link-loop
   * fix (AM-5706): a `no-pp-permission` (HTTP 401/403) on a refresh-derived
   * token is AMBIGUOUS (it can be the AM-5692 group-claim-dropping refresh
   * bug), so it re-links; the same 401 on a FRESH interactive-login token
   * (which always carries the group claim) can only mean genuine
   * ineligibility, so it must confirm the denial instead of re-linking
   * forever. See members/usePpCreateAuthPreflight.ts. Undefined on a stash
   * written before this field existed — treated as NOT interactive (i.e. the
   * conservative "could be a refresh" branch: re-link, never a false confirmed
   * denial).
   */
  fromInteractiveLogin?: boolean;
}

interface B2cTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  /** B2C returns these as STRINGS ("3600"); typed loosely so nothing trusts it. */
  expires_in: number | string;
  token_type: string;
  /**
   * B2C returns this on ROPC/refresh responses even though we historically
   * ignored it (ACT-1247 SSO-PP auth-linking §5b) — the REAL lifetime of the
   * refresh token, in seconds. Optional because older/unknown B2C policies
   * may omit it.
   */
  refresh_token_expires_in?: number | string;
}

const STORAGE_PREFIX = "member-360:b2c-tokens:";

function storageKey(id: AuthProviderId) {
  return `${STORAGE_PREFIX}${id}`;
}

/**
 * Token-stash change subscription.
 *
 * The stash lives in `localStorage`, OUTSIDE React state, and several
 * INDEPENDENT call sites derive from it at render time — `getPpToken` and
 * `hasPersistedPpLink` (ppToken.ts) both re-read it on every render rather
 * than caching. Nothing re-rendered those readers when the stash changed, so
 * a PP login performed by ONE component left every OTHER reader stuck on its
 * pre-login derivation until a full page reload. On Home that was visible as
 * `HomePpConnectPrompt` correctly hiding itself after sign-in while
 * `UrgentPatientsWidget` / `HospitalEdEventsWidget` — separate
 * `useWorklistAccessGate` instances, each with its own private re-render
 * counter — stayed on `"no-pp-session"` and rendered nothing.
 *
 * Notifying from the two writers below (the ONLY paths that mutate the stash:
 * `ropcLogin`, `ropcRefresh`, `AuthContext.logout`, `invalidatePpLink`) makes
 * every subscriber re-derive no matter who wrote it. Readers consume this via
 * `useSyncExternalStore`, which is why the snapshot is a monotonic VERSION
 * and not the stash itself: `getStoredTokens` parses a fresh object on every
 * call, so an object snapshot would never compare equal and would re-render
 * forever.
 */
const storedTokenListeners = new Set<() => void>();
let storedTokensVersion = 0;

export function subscribeStoredTokens(onChange: () => void): () => void {
  storedTokenListeners.add(onChange);
  return () => {
    storedTokenListeners.delete(onChange);
  };
}

export function getStoredTokensVersion(): number {
  return storedTokensVersion;
}

function notifyStoredTokensChanged(): void {
  storedTokensVersion += 1;
  for (const listener of storedTokenListeners) listener();
}

/**
 * Self-heal stashes written by the old string-concatenation bug (or any other
 * corruption): a non-number / implausible `expires_at` becomes 0 (expired), so
 * `getFreshAccessToken` falls through to the refresh token instead of trusting
 * a garbage far-future expiry — and the session is NOT dropped. A garbage
 * `refresh_token_expires_at` is removed (unknown, which never warns).
 */
function sanitizeStoredTokens(t: B2cTokens): B2cTokens {
  const out: B2cTokens = { ...t, expires_at: plausibleEpoch(t.expires_at) ?? 0 };
  if (plausibleEpoch(t.refresh_token_expires_at) === null) {
    delete out.refresh_token_expires_at;
  }
  return out;
}

export function getStoredTokens(id: AuthProviderId): B2cTokens | null {
  try {
    const raw = window.localStorage.getItem(storageKey(id));
    return raw ? sanitizeStoredTokens(JSON.parse(raw) as B2cTokens) : null;
  } catch {
    return null;
  }
}

export function setStoredTokens(id: AuthProviderId, tokens: B2cTokens): void {
  try {
    window.localStorage.setItem(storageKey(id), JSON.stringify(tokens));
    // Inside the `try`, after the write landed: a throwing `setItem` changed
    // nothing, so there is nothing for subscribers to re-derive.
    notifyStoredTokensChanged();
  } catch {
    /* noop */
  }
}

export function clearStoredTokens(id: AuthProviderId): void {
  try {
    window.localStorage.removeItem(storageKey(id));
    notifyStoredTokensChanged();
  } catch {
    /* noop */
  }
}

function tokenEndpoint(cfg: B2cConfig): string {
  return `https://${cfg.domainName}.b2clogin.com/${cfg.domainName}.onmicrosoft.com/${cfg.userFlow}/oauth2/v2.0/token`;
}

/**
 * Coerce a B2C lifetime ("3600" or 3600) to a finite number of seconds, or
 * null if it isn't one. `now + "3600"` is string concatenation, which once
 * stored a 14-char garbage `expires_at` that never expired.
 */
function toSeconds(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Longest lifetime we believe for any stored expiry; beyond it = corrupt. */
const MAX_PLAUSIBLE_AHEAD_SECONDS = 365 * 24 * 60 * 60;

/** A stored epoch-seconds expiry that is a finite number, not absurdly far out. */
function plausibleEpoch(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  if (v > Math.floor(Date.now() / 1000) + MAX_PLAUSIBLE_AHEAD_SECONDS) return null;
  return v;
}

function toTokens(res: B2cTokenResponse): B2cTokens {
  const now = Math.floor(Date.now() / 1000);
  const accessLifetime = toSeconds(res.expires_in);
  const refreshLifetime = toSeconds(res.refresh_token_expires_in);
  return {
    access_token: res.access_token,
    refresh_token: res.refresh_token,
    id_token: res.id_token,
    // Non-numeric lifetime -> 0 = already expired (forces a refresh/re-login
    // rather than trusting a token of unknown age).
    expires_at: accessLifetime === null ? 0 : now + accessLifetime,
    token_type: res.token_type,
    ...(refreshLifetime !== null
      ? { refresh_token_expires_at: now + refreshLifetime }
      : {}),
  };
}

/**
 * Extracts the B2C error message from a failed token response. B2C returns
 * `{ error_description: "AADB2CXXXXXX: ..." }` — the AADB2C prefix is
 * noisy and the remaining text is often still user-hostile
 * (`The username or password provided in the request are invalid.`), so
 * we also map the few codes a real user might hit to friendlier
 * copy. Codes not in the map fall back to the stripped description.
 */
const AADB2C_FRIENDLY_MESSAGES: Record<string, string> = {
  // Wrong username or password for ROPC flow
  AADB2C90225: "Incorrect email or password. Please try again.",
  AADB2C99002: "Incorrect email or password. Please try again.",
  // Account is locked after too many failed attempts
  AADB2C90149: "Too many failed attempts. Please wait a few minutes and try again.",
  AADB2C90157: "Too many failed attempts. Please wait a few minutes and try again.",
  // User not found / doesn't exist in the tenant
  AADB2C90024: "No account matches that email. Contact your administrator if this is wrong.",
  AADB2C99005: "No account matches that email. Contact your administrator if this is wrong.",
};

async function parseError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error_description?: string; error?: string };
    const raw = body.error_description ?? body.error ?? res.statusText;
    const codeMatch = raw.match(/^AADB2C(\d+):/);
    if (codeMatch) {
      const code = `AADB2C${codeMatch[1]}`;
      const mapped = AADB2C_FRIENDLY_MESSAGES[code];
      if (mapped) return mapped;
    }
    // Unknown code or no code at all — strip the AADB2C prefix and take
    // the first line, which is usually the short human-readable part.
    return raw.replace(/^AADB2C\d+:\s*/, "").split("\n")[0] ?? raw;
  } catch {
    return res.statusText || `HTTP ${res.status}`;
  }
}

export async function ropcLogin(
  cfg: B2cConfig,
  email: string,
  password: string,
  /**
   * `persist: false` returns the tokens WITHOUT writing the localStorage stash
   * or notifying subscribers. Used by the side-panel sign-in popup, which hands
   * the tokens to the iframe via postMessage and must not leave a second
   * long-lived copy (or clobber a tab session) in its own unpartitioned storage.
   */
  opts: { persist?: boolean } = {},
): Promise<B2cTokens> {
  const params = new URLSearchParams({
    grant_type: "password",
    client_id: cfg.clientId,
    username: email,
    password,
    response_type: "token id_token",
    scope: `openid ${cfg.clientId} offline_access`,
  });

  const res = await fetch(tokenEndpoint(cfg), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body: params.toString(),
  });

  if (!res.ok) {
    throw new Error(await parseError(res));
  }

  const body = (await res.json()) as B2cTokenResponse;
  // Bind to the email we actually authenticated with — NOT a decoded
  // id_token claim (see B2cTokens.boundIdentityEmail doc comment). This is
  // what makes ppToken.ts's cross-identity check independent of whatever
  // claims this B2C policy's id_token happens to emit.
  const tokens: B2cTokens = {
    ...toTokens(body),
    boundIdentityEmail: email.trim().toLowerCase(),
    // This IS an interactive login — the freshly-minted token carries the
    // full IPA group claim (unlike a silent refresh, which can drop it under
    // AM-5692). See B2cTokens.fromInteractiveLogin.
    fromInteractiveLogin: true,
  };
  if (opts.persist !== false) setStoredTokens(cfg.id, tokens);
  return tokens;
}

/**
 * CONCERN 2 fix (concurrent-refresh clobber): the mount-time silent mint
 * (`ppToken.ts`'s `ensurePpAccessToken`) and a user-initiated Create-Auth
 * click can both decide the stashed access token is expiring and race to
 * refresh it. If B2C rotates the refresh token on use (common for
 * `offline_access` grants), the SECOND request to reach B2C is handed a
 * refresh token B2C already invalidated — it gets back `invalid_grant`, and
 * the caller's failure handling (`clearStoredTokens`) wipes out the FIRST
 * request's perfectly valid new session, forcing a spurious re-login.
 *
 * Fixed with a simple in-flight promise mutex keyed by provider id: a
 * refresh already in flight for a given provider is shared by every
 * concurrent caller instead of firing a second grant request. This is safe
 * because both callers read the SAME `refresh_token` from the (not-yet-
 * rotated) stash before either one calls in — the second caller's own
 * `refreshToken` argument is therefore redundant with the in-flight call's,
 * and it gets the in-flight call's real result (success or failure) instead
 * of racing it.
 */
const inFlightRefreshes = new Map<AuthProviderId, Promise<B2cTokens>>();

export async function ropcRefresh(cfg: B2cConfig, refreshToken: string): Promise<B2cTokens> {
  const existing = inFlightRefreshes.get(cfg.id);
  if (existing) return existing;

  const promise = (async () => {
    const params = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: cfg.clientId,
      refresh_token: refreshToken,
      scope: `openid ${cfg.clientId} offline_access`,
    });

    const res = await fetch(tokenEndpoint(cfg), {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: params.toString(),
    });

    if (!res.ok) {
      throw new Error(await parseError(res));
    }

    const body = (await res.json()) as B2cTokenResponse;
    // A refresh never changes WHO the token belongs to — carry the existing
    // binding forward rather than dropping it (toTokens has no way to know
    // it; there's no email in a refresh_token grant).
    const previousBoundEmail = getStoredTokens(cfg.id)?.boundIdentityEmail;
    const tokens: B2cTokens = {
      ...toTokens(body),
      ...(previousBoundEmail ? { boundIdentityEmail: previousBoundEmail } : {}),
      // A refresh-derived token is, by definition, NOT from an interactive
      // login — it is the token the pre-flight's re-link-loop fix must treat
      // as ambiguous (AM-5692) rather than a confirmed-denial signal.
      fromInteractiveLogin: false,
    };
    setStoredTokens(cfg.id, tokens);
    return tokens;
  })();

  inFlightRefreshes.set(cfg.id, promise);
  try {
    return await promise;
  } finally {
    // Only clear if we're still the in-flight entry — a caller that raced
    // in after this one settled but before cleanup ran could otherwise
    // clobber a newer in-flight promise's map entry.
    if (inFlightRefreshes.get(cfg.id) === promise) {
      inFlightRefreshes.delete(cfg.id);
    }
  }
}

/** True if the access token expires in <60s (or is already expired). */
export function isExpiringSoon(tokens: B2cTokens, skewSeconds = 60): boolean {
  const exp = plausibleEpoch(tokens.expires_at);
  if (exp === null) return true; // garbage / non-numeric = treat as expired
  return exp - skewSeconds <= Math.floor(Date.now() / 1000);
}

/**
 * True if the REFRESH token itself is within `withinDays` of its own expiry
 * (ACT-1247 SSO-PP auth-linking design doc §5b — the "load-bearing config"
 * for the whole "link once" promise). Returns `false` — never warns — when
 * `refresh_token_expires_at` is unknown (a stash written before this field
 * existed, or a B2C response that omitted the claim): absence of data is not
 * evidence of imminent expiry. Not wired to any UI in Phase 1; captured here
 * so a future proactive re-link prompt (§5b "FE follow-on") has the data
 * without an extra round trip.
 */
export function isRefreshExpiringSoon(tokens: B2cTokens, withinDays = 7): boolean {
  const refreshExp = plausibleEpoch(tokens.refresh_token_expires_at);
  if (!refreshExp) return false;
  const withinSeconds = withinDays * 24 * 60 * 60;
  return refreshExp - withinSeconds <= Math.floor(Date.now() / 1000);
}

/**
 * Returns a non-expired access token, refreshing first if needed. Returns
 * null if there's no session or the refresh fails (caller should show login).
 */
export async function getFreshAccessToken(
  cfg: B2cConfig,
): Promise<string | null> {
  const current = getStoredTokens(cfg.id);
  if (!current) return null;
  if (!isExpiringSoon(current)) return current.access_token;
  if (!current.refresh_token) {
    clearStoredTokens(cfg.id);
    return null;
  }
  try {
    const refreshed = await ropcRefresh(cfg, current.refresh_token);
    return refreshed.access_token;
  } catch {
    clearStoredTokens(cfg.id);
    return null;
  }
}

/**
 * True only for a value that is actually an email address — used to keep an
 * opaque identifier out of a slot every caller treats as an email (see
 * `readB2cIdentityEmail`). Deliberately loose about the local part (B2C
 * accepts far more than RFC-common characters) and strict only about the
 * structure that distinguishes an email from an id: exactly one `@`, no
 * whitespace, and a dotted domain.
 */
function looksLikeEmail(value: string | undefined): value is string {
  return !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Decodes an id_token's best-available email claim (normalized: trim +
 * lowercase), for DISPLAY use only (e.g. AuthContext's `accountLabel`, shown
 * as "signed in as ..."). B2C policies are inconsistent about which claim
 * carries an email — Provider Portal's ROPC policy in particular doesn't emit
 * `emails`/`preferred_username` — so this tries a fallback chain across every
 * claim that might hold one.
 *
 * Every candidate is filtered through `looksLikeEmail`, so a claim carrying
 * something that ISN'T an email is skipped rather than returned. That matters
 * for `name`: under the Provider Portal ROPC policy it holds an opaque
 * PortalManager Key ID, and returning it surfaced that id as the AstraConnect
 * profile label (AM-5930). A `name` that genuinely holds an email still
 * resolves; one holding a Key ID (or a human display name) now yields `null`,
 * and the caller falls back to its own "no label" handling.
 *
 * Still must NOT be used to decide a security-relevant identity match: a
 * decoded claim is policy-dependent and, for the PP policy, absent entirely.
 * As of the ACT-1247 follow-up, `ppToken.ts`'s cross-identity check compares
 * against `B2cTokens.boundIdentityEmail` (the literal authenticated email,
 * set by `ropcLogin`), not a decoded claim — and as of AM-5930 so does
 * `AuthContext.accountLabel`, which now prefers that bound email and reaches
 * this decoder only for a session stored before the binding existed.
 */
export function readB2cIdentityEmail(idToken?: string): string | null {
  if (!idToken) return null;
  try {
    const payload = idToken.split(".")[1];
    if (!payload) return null;
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as {
      emails?: string[];
      preferred_username?: string;
      email?: string;
      name?: string;
      upn?: string;
      signInName?: string;
    };
    const candidate = [
      decoded.emails?.[0],
      decoded.preferred_username,
      decoded.email,
      decoded.signInName,
      decoded.upn,
      decoded.name,
    ].find(looksLikeEmail);
    return candidate ? candidate.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * The display label for a B2C-primary session (AuthContext's `accountLabel`) —
 * the user's email, or `null` when none can be established.
 *
 * Order matters (AM-5930): `boundIdentityEmail` is the email the session was
 * literally AUTHENTICATED with, so it holds for every policy shape, whereas
 * the id_token is only as good as the claims its policy emits — the Provider
 * Portal ROPC policy emits no email claim at all. Decoding first meant PP
 * sessions fell through to the `name` claim, which carries an opaque
 * PortalManager Key ID, and that id was what the profile menu displayed. The
 * decoder stays as the fallback for a session stored before the binding
 * existed (`ropcRefresh` can't invent one it never had), and no longer
 * returns a non-email claim, so the worst case is `null` — which callers
 * render as their own placeholder — never an id shown as a username.
 */
export function readB2cAccountLabel(
  tokens: B2cTokens | null | undefined,
): string | null {
  if (!tokens) return null;
  return tokens.boundIdentityEmail ?? readB2cIdentityEmail(tokens.id_token);
}
